import { ContactType } from '@/app/types/ContactType'
import { MemberReponse } from '@/app/types/memberType'
import { toast } from 'sonner'
import { apiSlice } from './apiSlice'

export const memberSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMemberServices: builder.query<MemberReponse, void>({
      query: () => ({
        url: '/member',
        method: 'GET',
      }),
      providesTags: ['Member'],
      transformResponse: (responseData: MemberReponse) => {
        return {
          ...responseData,
          members: responseData.members.map((item) => ({
            ...item,
            emails:
              item.user.contact.contact_methods?.filter(
                (method) => method.contact_type === 'email'
              ) || [],
            phones:
              item.user.contact.contact_methods?.filter(
                (method) => method.contact_type === 'phone'
              ) || [],
            mobiles:
              item.user.contact.contact_methods?.filter(
                (method) => method.contact_type === 'mobile'
              ) || [],
          })),
          unverified_members: responseData.unverified_members.map((item) => ({
            ...item,
            emails:
              item.user.contact.contact_methods?.filter(
                (method) => method.contact_type === 'email'
              ) || [],
            phones:
              item.user.contact.contact_methods?.filter(
                (method) => method.contact_type === 'phone'
              ) || [],
            mobiles:
              item.user.contact.contact_methods?.filter(
                (method) => method.contact_type === 'mobile'
              ) || [],
          })),
        }
      },
    }),
    getSingleMember: builder.query({
      query: ({ memberId }: { memberId: number }) => ({
        url: `/member/${memberId}`,
        method: 'GET',
      }),
    }),
    addMemberServices: builder.mutation({
      query: (member: FormData) => ({
        url: '/member',
        method: 'POST',
        body: member,
        credentials: 'include',
      }),
      invalidatesTags: ['Member'],
      transformErrorResponse: async (responseData: any) => {
        toast.error(responseData?.data?.errors)
        return responseData
      },
    }),
    updateMemberServices: builder.mutation({
      query: (data: { id: number; member: FormData }) => ({
        url: `/member/${data.id}`,
        method: 'PATCH',
        body: data.member,
      }),
      invalidatesTags: ['Member'],
      transformErrorResponse: async (responseData: any) => {
        toast.error(responseData?.data?.errors)
        return responseData
      },
    }),
    deleteMemberServices: builder.mutation({
      query: (id) => ({
        url: `/member/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Member'],
      transformErrorResponse: async (responseData: any) => {
        toast.error(responseData?.data?.errors?.[0])
        return responseData
      },
    }),
    acceptMemberServices: builder.mutation({
      query: (id) => ({
        url: `/member/${id}/verify`,
        method: 'POST',
      }),
      invalidatesTags: ['Member'],
      transformErrorResponse: async (responseData: any) => {
        toast.error(responseData?.data?.errors)
        return responseData
      },
    }),
    sendRoleUpgrade: builder.mutation({
      query: ({
        adminId,
        roleChangeMessage,
        userMessage,
      }: {
        adminId: number
        roleChangeMessage: string
        userMessage: string
      }) => ({
        url: '/member/request_role_change',
        method: 'POST',
        body: {
          admin_id: adminId,
          role_change_message: roleChangeMessage,
          user_message: userMessage,
        },
      }),
    }),
    getMemberNewsPreference: builder.query({
      query: () => `/member/news_preferences`,
      transformResponse: async (responseData) => {
        return responseData
      },
    }),
    getCompanyMember: builder.query<
      ContactType,
      { companyFriendlyUrl: string }
    >({
      query: ({ companyFriendlyUrl }) => ({
        method: 'GET',
        url: `/companies/${companyFriendlyUrl}/contacts`,
      }),
      transformResponse: (response: ContactType) => {
        return {
          ...response,
          contacts:
            response?.contacts?.map((item) => ({
              ...item,
              emails:
                item.contact_methods?.filter(
                  (method) => method.contact_type === 'email'
                ) || [],
              phones:
                item.contact_methods?.filter(
                  (method) => method.contact_type === 'phone'
                ) || [],
              mobiles:
                item.contact_methods?.filter(
                  (method) => method.contact_type === 'mobile'
                ) || [],
            })) || [],
        }
      },
    }),
    newsPreferenceServices: builder.mutation({
      query: (data) => ({
        url: `/member/news_preferences`,
        method: 'POST',
        body: data,
      }),
      transformErrorResponse: async (responseData: any) => {
        toast.error(responseData?.data?.message)
        return responseData
      },
    }),
  }),
})

export const {
  useGetMemberServicesQuery,
  useAddMemberServicesMutation,
  useUpdateMemberServicesMutation,
  useDeleteMemberServicesMutation,
  useAcceptMemberServicesMutation,
  useSendRoleUpgradeMutation,
  useGetMemberNewsPreferenceQuery,
  useNewsPreferenceServicesMutation,
  useGetSingleMemberQuery,
  useGetCompanyMemberQuery,
} = memberSlice
